import React, { useEffect, useMemo, useRef, useState } from 'react'

// TODO: eliminate imports from "old" code paths (components/..., lib/...}, move things to V2
// V1 imports
import { Alert, LinearProgress, Typography } from '@barracuda-internal/bds-core'
import { useAppDispatch, useAppSelector } from 'redux/toolkit/hooks'
import { Domain } from 'types/domains'
import { getVerifiedDomainList, reset as resetDomainsStore } from 'redux/features/domains/domainsSlice'
import { isFailed, isSuccess } from 'redux/toolkit/api'
import { SettingsPageContainer } from 'components/libs/layout/pages/SettingsPageContainer'
import { PageTitle } from 'components/libs/layout/pages/PageTitle'
import { useFormatMessage } from 'lib/localization'
import { FormSpacer } from 'components/libs/layout/pages/FormSpacer'
import { PageSubtitle } from 'components/libs/layout/pages/PageSubtitle'
import outboundSettingsStyles from 'components/pages/outboundSettings/outboundSettingsStyles'
import appConfig from 'config/appConfig'

// V2 module imports
import { Auth, AuthSubject, EgdApiClient, IAuthPolicy, UrlUtils, useI18n } from 'v2/modules/lib'
import { NotificationsPage, NotificationsPageProps } from './NotificationsPage'
import { NotificationsForm } from './notificationsForm'

type UIStatePending = { state: 'pending' }
type UIStateReady = { state: 'ready'; props: NotificationsPageProps }
type UIStateError = { state: 'error'; error: unknown }

type UIState = UIStatePending | UIStateReady | UIStateError

const BASE_I18N_KEY = 'ess.outbound_settings.notifications'

export const NotificationsPageLoader: React.FC = () => {
  const dispatch = useAppDispatch()
  const formatMessage = useFormatMessage(BASE_I18N_KEY)
  const classes = outboundSettingsStyles()

  const { t } = useI18n()
  const canSetState = useRef(true)
  const [loadedVerifiedDomains, setLoadedVerifiedDomains] = useState<Domain[]>()
  const [uiState, setUiState] = useState<UIState>({ state: 'pending' })

  // TODO: switch to load dependencies via v2 API
  const {
    accessToken,
    accessTokenObject,
    verifiedDomainList,
    isGetVerifiedDomainsSuccess,
    isGetVerifiedDomainsFailed
  } = useAppSelector(_store => ({
    accessToken: _store.auth.accessToken,
    accessTokenObject: _store.auth.accessTokenObject,
    verifiedDomainList: _store.domains.verifiedDomainList,
    isGetVerifiedDomainsSuccess: isSuccess(_store.domains.api.getVerifiedDomainListApiStatus),
    isGetVerifiedDomainsFailed: isFailed(_store.domains.api.getVerifiedDomainListApiStatus)
  }))

  useEffect(() => {
    canSetState.current = true
    return () => {
      canSetState.current = false
    }
  }, [])

  useEffect(() => {
    // TODO: port getting domain list to v2 egdApiClient
    //       v1 API won't allow read access to getVerifiedDomains
    //       v2 account-service should expose a get-domains endpoint
    if (accessTokenObject?.pdDomainId) {
      // Check domain-level policy access when in domain mode - don't fetch if no access
      const domainId = Number(accessTokenObject.pdDomainId)
      const authFromRedux = Auth((accessTokenObject.authDomainPolicies || []) as IAuthPolicy[])
      const canReadDomainPolicies = authFromRedux.checkPolicy({
        subject: AuthSubject.DOMAIN_LEVEL_POLICIES,
        value: { domain_id: domainId },
        action: 'R'
      })
      const canManageDomainPolicies = authFromRedux.checkPolicy({
        subject: AuthSubject.DOMAIN_LEVEL_POLICIES,
        value: { domain_id: domainId },
        action: 'U'
      })
      if (!canReadDomainPolicies && !canManageDomainPolicies) {
        setUiState({
          state: 'error',
          error: 'no_domain_policy_access'
        })
        return
      }
      setLoadedVerifiedDomains([])
      return
    }
    dispatch(resetDomainsStore())
    dispatch(getVerifiedDomainList())
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (isGetVerifiedDomainsSuccess) {
      setLoadedVerifiedDomains(verifiedDomainList)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGetVerifiedDomainsSuccess])

  useEffect(() => {
    if (isGetVerifiedDomainsFailed) {
      setLoadedVerifiedDomains(verifiedDomainList)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGetVerifiedDomainsSuccess])

  useEffect(() => {
    if (accessToken && accessTokenObject && loadedVerifiedDomains) {
      // eslint-disable-next-line @typescript-eslint/no-extra-semi
      ;(async () => {
        try {
          const domainId = accessTokenObject.pdDomainId ? Number(accessTokenObject.pdDomainId) : null
          const allowedDomainNames = accessTokenObject.pdDomainName
            ? [accessTokenObject.pdDomainName]
            : loadedVerifiedDomains.map(d => d.domainName)
          const { accountId } = accessTokenObject
          const urlUtils = UrlUtils()
          const egdApiClient = EgdApiClient({
            host: `${appConfig.GATEWAY_URL}/v2`,
            commonHeaders: {
              Authorization: accessToken ? `Bearer ${accessToken}` : undefined
            },
            urlUtils
          })
          const availableAuthPolicies = await egdApiClient.authService.getAvailableAuthPolicies()
          const auth = Auth(availableAuthPolicies as IAuthPolicy[])
          const settingsForm = NotificationsForm({ egdApiClient, accountId, domainId, allowedDomainNames, auth })
          if (!canSetState.current) {
            return
          }
          if (!settingsForm.form.canRead) {
            setUiState({
              state: 'error',
              error: 'unauthorized'
            })
            return
          }
          setUiState({
            state: 'ready',
            props: {
              settingsForm,
              domainId,
              egdApiClient,
              accountId
            }
          })
        } catch {
          if (!canSetState.current) {
            return
          }
          setUiState({
            state: 'error',
            error: 'failed_to_load_dependencies'
          })
        }
      })()
    }
  }, [accessToken, accessTokenObject, loadedVerifiedDomains])

  // TODO: when switching the UI to BDS v2 improve/standardize the UI state loading phases
  return useMemo(() => {
    switch (uiState.state) {
      case 'ready':
        return <NotificationsPage {...uiState.props} />
      case 'error':
        return (
          <SettingsPageContainer isContentLoading={false} isFailedToLoad={false}>
            <PageTitle title={formatMessage('title')} />
            <FormSpacer />
            <PageSubtitle>
              {formatMessage('admin_quarantine_notification')}
              <Typography variant="body1" className={classes.italic} data-testid="quarantine-description">
                {formatMessage('admin_quarantine_notification_description')}
              </Typography>
            </PageSubtitle>
            <FormSpacer />
            <Alert severity="error" style={{ width: '100%' }}>
              {t(`page_loader_errors.${uiState.error}`)}
            </Alert>
          </SettingsPageContainer>
        )
      case 'pending':
      default:
        return (
          <SettingsPageContainer isContentLoading={false} isFailedToLoad={false}>
            <PageTitle title={formatMessage('title')} />
            <FormSpacer />
            <PageSubtitle>
              {formatMessage('admin_quarantine_notification')}
              <Typography variant="body1" className={classes.italic} data-testid="quarantine-description">
                {formatMessage('admin_quarantine_notification_description')}
              </Typography>
            </PageSubtitle>
            <FormSpacer />
            <LinearProgress style={{ width: '100%' }} />
          </SettingsPageContainer>
        )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uiState.state])
}
